import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
    Plus,
    Search,
    History,
    ArrowUpRight,
    ArrowDownLeft,
    HandCoins,
    Store,
    User,
    X,
    TrendingUp,
    Users,
    CirclePlus, // Added for inline credit addition
    Trash2     // Added for account deletion
} from "lucide-react";
import { supabase } from "../../lib/supabase";

export default function CreditManager() {
    const [credits, setCredits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");

    // Modals & Drawers State
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isPayModalOpen, setIsPayModalOpen] = useState(false);
    const [isAddCreditModalOpen, setIsAddCreditModalOpen] = useState(false); // New modal state
    const [activeHistoryAccount, setActiveHistoryAccount] = useState(null);
    const [historyLogs, setHistoryLogs] = useState([]);

    // Form States
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentRemarks, setPaymentRemarks] = useState("");
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    
    // Form States for adding credit to existing account
    const [creditAmount, setCreditAmount] = useState("");
    const [creditRemarks, setCreditRemarks] = useState("");
    const [creditDate, setCreditDate] = useState(new Date().toISOString().split('T')[0]);

    const [newAccount, setNewAccount] = useState({
        member_name: "",
        shop_name: "",
        phone: "",
        initial_due: ""
    });

    useEffect(() => {
        fetchCredits();
    }, []);

    const fetchCredits = async () => {
        try {
            setLoading(true);
            const { data, error } = await supabase
                .from("credits")
                .select("*")
                .order("created_at", { ascending: false });

            if (error) throw error;
            setCredits(data || []);
        } catch (error) {
            toast.error("Failed to load credit details");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAccount = async (e) => {
        e.preventDefault();
        if (!newAccount.member_name) return toast.error("Member Name is required");

        try {
            const initialDueAmount = parseFloat(newAccount.initial_due) || 0;

            // 1. Insert into credits table
            const { data: createdCredit, error: creditError } = await supabase
                .from("credits")
                .insert([{
                    member_name: newAccount.member_name,
                    shop_name: newAccount.shop_name || null,
                    phone: newAccount.phone || null,
                    total_due: initialDueAmount
                }])
                .select()
                .single();

            if (creditError) throw creditError;

            // 2. If there is an initial due, log it down in transactions
            if (initialDueAmount > 0 && createdCredit) {
                await supabase.from("credit_logs").insert([{
                    credit_id: createdCredit.id,
                    amount: initialDueAmount,
                    type: "borrowed",
                    remarks: "Initial setup balance"
                }]);
            }

            toast.success("Credit profile added successfully");
            setIsAddModalOpen(false);
            setNewAccount({ member_name: "", shop_name: "", phone: "", initial_due: "" });
            fetchCredits();
        } catch (error) {
            toast.error("Could not register account");
            console.error(error);
        }
    };

    const handleMakePayment = async (e) => {
        e.preventDefault();
        const amountToPay = parseFloat(paymentAmount);
        if (!amountToPay || amountToPay <= 0) return toast.error("Enter a valid amount");
        if (amountToPay > selectedAccount.total_due) return toast.error("Payment exceeds outstanding due amount!");

        try {
            const targetTimestamp = new Date(`${paymentDate}T${new Date().toTimeString().split(' ')[0]}`).toISOString();

            const { error: logError } = await supabase.from("credit_logs").insert([{
                credit_id: selectedAccount.id,
                amount: amountToPay,
                type: "returned",
                remarks: paymentRemarks || "Settlement return payment",
                created_at: targetTimestamp
            }]);

            if (logError) throw logError;

            const updatedBalance = selectedAccount.total_due - amountToPay;
            const { error: updateError } = await supabase
                .from("credits")
                .update({ total_due: updatedBalance })
                .eq("id", selectedAccount.id);

            if (updateError) throw updateError;

            toast.success(`Successfully cleared ₹${amountToPay}`);
            setIsPayModalOpen(false);
            setPaymentAmount("");
            setPaymentRemarks("");
            setPaymentDate(new Date().toISOString().split('T')[0]);
            fetchCredits();
        } catch (error) {
            toast.error("Failed to post balance execution");
            console.error(error);
        }
    };

    // New handler function to add extra credit to an existing account
    const handleAddCredit = async (e) => {
        e.preventDefault();
        const amountToCredit = parseFloat(creditAmount);
        if (!amountToCredit || amountToCredit <= 0) return toast.error("Enter a valid amount");

        try {
            const targetTimestamp = new Date(`${creditDate}T${new Date().toTimeString().split(' ')[0]}`).toISOString();

            // 1. Log the new credit addition event
            const { error: logError } = await supabase.from("credit_logs").insert([{
                credit_id: selectedAccount.id,
                amount: amountToCredit,
                type: "borrowed",
                remarks: creditRemarks || "Additional credit added",
                created_at: targetTimestamp
            }]);

            if (logError) throw logError;

            // 2. Update running balance
            const updatedBalance = parseFloat(selectedAccount.total_due || 0) + amountToCredit;
            const { error: updateError } = await supabase
                .from("credits")
                .update({ total_due: updatedBalance })
                .eq("id", selectedAccount.id);

            if (updateError) throw updateError;

            toast.success(`Successfully added ₹${amountToCredit} credit`);
            setIsAddCreditModalOpen(false);
            setCreditAmount("");
            setCreditRemarks("");
            setCreditDate(new Date().toISOString().split('T')[0]);
            fetchCredits();
        } catch (error) {
            toast.error("Failed to add credit record");
            console.error(error);
        }
    };

    // New handler to delete entire profile record
    const handleDeleteAccount = async (account) => {
        const confirmMessage = `Are you absolutely sure you want to completely delete the ledger profile for "${account.member_name}"?\n\nThis action cannot be undone and will erase all transaction statements history automatically.`;
        if (!window.confirm(confirmMessage)) return;

        try {
            const { error } = await supabase
                .from("credits")
                .delete()
                .eq("id", account.id);

            if (error) throw error;

            toast.success("Account profile wiped successfully");
            fetchCredits();
        } catch (error) {
            toast.error("Failed to clear ledger entry");
            console.error(error);
        }
    };

    const viewHistory = async (account) => {
        setActiveHistoryAccount(account);
        try {
            const { data, error } = await supabase
                .from("credit_logs")
                .select("*")
                .eq("credit_id", account.id)
                .order("created_at", { ascending: false });

            if (error) throw error;
            setHistoryLogs(data || []);
        } catch (error) {
            toast.error("Failed to retrieve operational statements");
        }
    };

    const totalOutstanding = credits.reduce((acc, curr) => acc + (curr.total_due || 0), 0);
    const activeCreditors = credits.filter(c => c.total_due > 0).length;

    const filteredCredits = credits.filter(item =>
        item.member_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.shop_name && item.shop_name.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div className="p-4 lg:p-10 space-y-8 max-w-[1600px] mx-auto min-h-screen">

            {/* --- Top Dashboard Header --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h2 className="text-xl lg:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                        <HandCoins className="text-[#0054A6]" size={26} />
                        Credit Ledger
                    </h2>
                    <p className="text-xs text-slate-500 font-medium mt-1">
                        Track industrial vendors, supply credits, and payout histories for Shree Construction accounts.
                    </p>
                </div>

                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="bg-[#0054A6] hover:bg-blue-700 text-white font-bold text-sm px-4 py-3 rounded-xl flex items-center gap-2 shadow-lg shadow-blue-100 transition-all self-stretch sm:self-auto justify-center"
                >
                    <Plus size={18} />
                    Create Credit Profile
                </button>
            </div>

            {/* --- KPI Summaries --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Total Payable Balance</p>
                        <p className="text-2xl font-black text-slate-900">₹{totalOutstanding.toLocaleString("en-IN")}</p>
                    </div>
                    <div className="p-3 bg-red-50 text-[#EE3124] rounded-xl"><TrendingUp size={24} /></div>
                </div>

                <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Active Outstandings</p>
                        <p className="text-2xl font-black text-slate-900">{activeCreditors} Accounts</p>
                    </div>
                    <div className="p-3 bg-blue-50 text-[#0054A6] rounded-xl"><Users size={24} /></div>
                </div>

                <div className="bg-white border border-slate-200 p-6 rounded-2xl shadow-sm flex items-center justify-between">
                    <div className="space-y-1">
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Shree Region Scope</p>
                        <p className="text-2xl font-black text-slate-900">Lucknow Hub</p>
                    </div>
                    <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl"><Store size={24} /></div>
                </div>
            </div>

            {/* --- Filter & Action Bar --- */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="relative w-full md:w-96">
                    <Search className="absolute left-3.5 top-3.5 text-slate-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search by vendor name or enterprise shop..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-medium placeholder-slate-400 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0054A6] transition-all"
                    />
                </div>
            </div>

            {/* --- Creditors List / Main Table Grid --- */}
            {loading ? (
                <div className="text-center py-12 font-bold text-slate-400">Syncing database registers...</div>
            ) : filteredCredits.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center text-slate-400 font-medium">
                    No credit accounts match your lookup rules.
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-black tracking-wider text-slate-400 uppercase">
                                    <th className="py-4 px-6">Party Information</th>
                                    <th className="py-4 px-6">Firm/Shop Name</th>
                                    <th className="py-4 px-6">Phone Number</th>
                                    <th className="py-4 px-6 text-right">Outstanding Due</th>
                                    <th className="py-4 px-6 text-center">Operational Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-sm font-medium text-slate-700">
                                {filteredCredits.map((account) => (
                                    <tr key={account.id} className="hover:bg-slate-50/70 transition-colors">
                                        <td className="py-4 px-6 font-black text-slate-900">{account.member_name}</td>
                                        <td className="py-4 px-6 text-slate-500">
                                            {account.shop_name ? (
                                                <span className="flex items-center gap-1.5"><Store size={14} /> {account.shop_name}</span>
                                            ) : (
                                                <span className="text-xs text-slate-300 font-normal">Not Registered</span>
                                            )}
                                        </td>
                                        <td className="py-4 px-6 text-slate-500">{account.phone || "—"}</td>
                                        <td className="py-4 px-6 text-right font-bold text-base text-slate-900">
                                            <span className={account.total_due > 0 ? "text-[#EE3124]" : "text-emerald-600"}>
                                                ₹{parseFloat(account.total_due || 0).toLocaleString("en-IN")}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6">
                                            <div className="flex items-center justify-center gap-1.5">
                                                <button
                                                    disabled={account.total_due <= 0}
                                                    onClick={() => { setSelectedAccount(account); setIsPayModalOpen(true); }}
                                                    className="px-2.5 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 font-bold text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                                                >
                                                    Clear Due
                                                </button>
                                                
                                                {/* NEW: Dynamic Add Credit Balance Button */}
                                                <button
                                                    onClick={() => { setSelectedAccount(account); setIsAddCreditModalOpen(true); }}
                                                    className="p-1.5 bg-blue-50 text-[#0054A6] rounded-lg hover:bg-blue-100 transition-colors"
                                                    title="Add Credit Balance"
                                                >
                                                    <CirclePlus size={16} />
                                                </button>

                                                <button
                                                    onClick={() => viewHistory(account)}
                                                    className="p-1.5 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
                                                    title="Statement Ledger Logs"
                                                >
                                                    <History size={16} />
                                                </button>

                                                {/* NEW: Core Account Erase Profile Button */}
                                                <button
                                                    onClick={() => handleDeleteAccount(account)}
                                                    className="p-1.5 bg-red-50 text-[#EE3124] rounded-lg hover:bg-red-100 transition-colors"
                                                    title="Delete Profile Account"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* --- MODAL: Add New Account Profile --- */}
            {isAddModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl relative animate-in zoom-in-95 duration-200">
                        <button onClick={() => setIsAddModalOpen(false)} className="absolute top-4 right-4 p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
                        <h3 className="text-lg font-black text-slate-900 mb-5 flex items-center gap-2"><User size={20} className="text-[#0054A6]" /> Add Credit Profile</h3>
                        <form onSubmit={handleCreateAccount} className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Party Member Name *</label>
                                <input type="text" required value={newAccount.member_name} onChange={e => setNewAccount({ ...newAccount, member_name: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0054A6]" placeholder="e.g. Rakesh Kumar Sariawala" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Shop Name (Optional)</label>
                                <input type="text" value={newAccount.shop_name} onChange={e => setNewAccount({ ...newAccount, shop_name: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0054A6]" placeholder="e.g. Bajrang Iron Traders" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Phone Number</label>
                                <input type="tel" value={newAccount.phone} onChange={e => setNewAccount({ ...newAccount, phone: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0054A6]" placeholder="10-digit mobile line" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Initial Due Amount (₹)</label>
                                <input type="number" value={newAccount.initial_due} onChange={e => setNewAccount({ ...newAccount, initial_due: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0054A6]" placeholder="0.00" />
                            </div>
                            <button type="submit" className="w-full mt-2 bg-[#0054A6] text-white font-bold text-sm py-3 rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-100">Register Party Ledger</button>
                        </form>
                    </div>
                </div>
            )}

            {/* --- MODAL: Make Due Settlement Payment --- */}
            {isPayModalOpen && selectedAccount && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl relative animate-in zoom-in-95 duration-200">
                        <button onClick={() => setIsPayModalOpen(false)} className="absolute top-4 right-4 p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
                        <h3 className="text-lg font-black text-slate-900 mb-2 flex items-center gap-2"><ArrowDownLeft size={22} className="text-emerald-600" /> Post Return Payment</h3>
                        <p className="text-xs text-slate-500 font-medium mb-5">Paying down balance for <span className="font-bold text-slate-800">{selectedAccount.member_name}</span>. Current total outstanding matches <span className="text-[#EE3124] font-bold">₹{selectedAccount.total_due}</span>.</p>

                        <form onSubmit={handleMakePayment} className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Amount to pay (₹) *</label>
                                <input type="number" required max={selectedAccount.total_due} value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 mt-1 font-bold text-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600" placeholder="0.00" />
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Transaction Date *</label>
                                <input
                                    type="date"
                                    required
                                    value={paymentDate}
                                    onChange={e => setPaymentDate(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 mt-1 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0054A6]"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Transaction Notes / Remarks</label>
                                <input type="text" value={paymentRemarks} onChange={e => setPaymentRemarks(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0054A6]" placeholder="e.g. Transferred via GPay / Hand Cash given by supervisor" />
                            </div>

                            <button type="submit" className="w-full mt-2 bg-emerald-600 text-white font-bold text-sm py-3 rounded-xl hover:bg-emerald-700 transition-colors shadow-lg shadow-emerald-100">Submit Payment Record</button>
                        </form>
                    </div>
                </div>
            )}

            {/* --- NEW MODAL: Add Credit Balance to Existing Account --- */}
            {isAddCreditModalOpen && selectedAccount && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl relative animate-in zoom-in-95 duration-200">
                        <button onClick={() => setIsAddCreditModalOpen(false)} className="absolute top-4 right-4 p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
                        <h3 className="text-lg font-black text-slate-900 mb-2 flex items-center gap-2"><ArrowUpRight size={22} className="text-[#0054A6]" /> Add Credit Amount</h3>
                        <p className="text-xs text-slate-500 font-medium mb-5">Increasing payable balance for <span className="font-bold text-slate-800">{selectedAccount.member_name}</span>.</p>

                        <form onSubmit={handleAddCredit} className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Credit Amount to add (₹) *</label>
                                <input type="number" required value={creditAmount} onChange={e => setCreditAmount(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 mt-1 font-bold text-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0054A6]" placeholder="0.00" />
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Transaction Date *</label>
                                <input
                                    type="date"
                                    required
                                    value={creditDate}
                                    onChange={e => setCreditDate(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 mt-1 text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0054A6]"
                                />
                            </div>

                            <div>
                                <label className="text-xs font-bold text-slate-500 uppercase">Transaction Notes / Remarks</label>
                                <input type="text" value={creditRemarks} onChange={e => setCreditRemarks(e.target.value)} className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[#0054A6]" placeholder="e.g. Received extra batch of aggregate / Saria material load" />
                            </div>

                            <button type="submit" className="w-full mt-2 bg-[#0054A6] text-white font-bold text-sm py-3 rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-100">Confirm Credit Addition</button>
                        </form>
                    </div>
                </div>
            )}

            {/* --- SIDE DRAWER: Statement Activity History Logs --- */}
            {activeHistoryAccount && (
                <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/30 backdrop-blur-xs">
                    <div className="absolute inset-0" onClick={() => setActiveHistoryAccount(null)} />
                    <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">

                        {/* Drawer Header */}
                        <div className="p-6 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                            <div>
                                <h3 className="font-black text-slate-900 text-base">{activeHistoryAccount.member_name}</h3>
                                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-tight mt-0.5">{activeHistoryAccount.shop_name || "Independent Supplier"}</p>
                            </div>
                            <button onClick={() => setActiveHistoryAccount(null)} className="p-2 text-slate-400 hover:bg-slate-200 rounded-xl transition-colors"><X size={20} /></button>
                        </div>

                        {/* History Logs Output List */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Transaction History</p>
                            {historyLogs.length === 0 ? (
                                <div className="text-center py-12 text-slate-300 text-sm">No transaction statements loaded.</div>
                            ) : (
                                historyLogs.map((log) => (
                                    <div key={log.id} className="border border-slate-100 rounded-xl p-4 flex gap-3 items-start bg-slate-50/50">
                                        <div className={`p-2 rounded-lg shrink-0 ${log.type === "borrowed" ? "bg-red-50 text-[#EE3124]" : "bg-emerald-50 text-emerald-600"}`}>
                                            {log.type === "borrowed" ? <ArrowUpRight size={16} /> : <ArrowDownLeft size={16} />}
                                        </div>
                                        <div className="space-y-1 min-w-0 flex-1">
                                            <div className="flex justify-between items-baseline gap-2">
                                                <p className="text-xs font-bold uppercase text-slate-400 tracking-tighter">
                                                    {log.type === "borrowed" ? "Balance Borrowed" : "Return Payment"}
                                                </p>
                                                <p className={`font-black text-sm ${log.type === "borrowed" ? "text-[#EE3124]" : "text-emerald-600"}`}>
                                                    {log.type === "borrowed" ? "+" : "-"} ₹{parseFloat(log.amount).toLocaleString("en-IN")}
                                                </p>
                                            </div>
                                            <p className="text-sm font-medium text-slate-700 truncate">{log.remarks || "No remarks loaded"}</p>
                                            <p className="text-[10px] text-slate-400 font-medium">
                                                {new Date(log.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                                            </p>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}